import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Button,
  InputText,
  SectionCard,
  Select,
  TextArea,
  type SelectOption,
} from "@/deps/ui-library";
import { api } from "@/api";
import {
  useAccounts,
  useBankAccounts,
  useBillingItems,
  useInvoiceSetting,
  usePartyDetails,
  useSaveInvoiceDraft,
} from "@/api/queries";
import type { Invoice, Party, ReceiptMethod } from "@/api/types";
import { LineEditor, type EditableLine } from "@/components/LineEditor/LineEditor";
import { PageHeader } from "@/components/PageHeader";
import { PartyCombobox } from "@/components/PartyCombobox";
import { TotalsBlock, VatBreakdownTable } from "@/components/TotalsBlock";
import { CreditLimitWarning } from "@/features/parties/CreditLimitWarning";
import { usePartyFromAddress } from "@/features/parties/usePartyFromAddress";
import { calculateTotals } from "@/lib/invoiceTotals";
import { sellingAccounts } from "@/lib/sellingAccounts";
import { PAYMENT_TERMS, dueFrom } from "@/lib/paymentTerms";
import { useTaxWords } from "@/app/session";
import { CashReceive } from "./CashReceive";
import {
  DocumentAdjustments,
  adjustmentIsComplete,
  type EditableAdjustment,
} from "./DocumentAdjustments";

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Enter an invoice into the books (ADR-0027). This records it; it is not IRD-approved VAT billing.
 * It saves a draft, and issuing happens on the invoice itself: whoever enters a document is not the
 * one who issues it (build plan 2.1), so the two are different people's clicks.
 *
 * Given a draft and its customer, it edits that draft.
 */
export function NewInvoicePage({
  editing,
}: { editing?: { invoice: Invoice; customer: Party } } = {}) {
  const say = useTaxWords();
  const navigate = useNavigate();
  const { data: chart } = useAccounts();
  const revenueAccounts = sellingAccounts(chart);
  const { data: billingItems } = useBillingItems({});
  const { data: setting } = useInvoiceSetting();
  const { data: bankAccounts } = useBankAccounts();
  const save = useSaveInvoiceDraft();

  const draft = editing?.invoice;
  const back = draft
    ? ({ to: "/sales/invoices/$id", params: { id: draft.id } } as const)
    : ({ to: "/sales/invoices" } as const);

  const [customer, setCustomer] = useState<Party | null>(editing?.customer ?? null);
  usePartyFromAddress("customer", setCustomer);
  const [date, setDate] = useState(draft?.date ?? today());
  // Terms set the due date (ADR-0039): the invoice's, or else the customer's, or none, and a date
  // typed here stands. A draft keeps the date and the terms it was saved with (ADR-0041).
  const [termsOnInvoice, setTermsOnInvoice] = useState<string | null>(
    draft ? (draft.paymentTermsDays ?? "") : null,
  );
  const [typedDue, setTypedDue] = useState<string | null>(draft ? (draft.due ?? "") : null);
  const { data: customerDetails } = usePartyDetails(customer?.id ?? "", customer !== null);
  const terms = termsOnInvoice ?? customerDetails?.paymentTermsDays ?? "";
  const due = typedDue ?? dueFrom(date, terms);
  const [reference, setReference] = useState(draft?.buyerReference ?? "");
  const [note, setNote] = useState(draft?.note ?? "");
  const [lines, setLines] = useState<EditableLine[]>(
    draft
      ? draft.lines.map((line) => ({
          id: String(line.n),
          description: line.itemName,
          accountId: line.accountId,
          category: line.vatCategory,
          rate: line.vatRatePct,
          grossPrice: line.unitPrice,
          quantity: line.quantity,
          itemCode: line.itemCode,
          exemptionReason: line.exemptionReason,
          billingItemId: line.billingItemId,
          charges: line.charges,
          allowances: line.allowances,
        }))
      : [
          {
            id: "1",
            description: "",
            accountId: "",
            category: "S",
            rate: "13",
            grossPrice: "",
            quantity: "1",
          },
        ],
  );

  /** The chart arrives after the first render, so fill the gap during it. */
  const withAccounts = lines.map((line) =>
    line.accountId === "" ? { ...line, accountId: revenueAccounts[0]?.id ?? "" } : line,
  );
  const [adjustments, setAdjustments] = useState<EditableAdjustment[]>(
    (draft?.adjustments ?? []).map((adjustment) => ({
      ...adjustment,
      id: crypto.randomUUID(),
    })),
  );
  const totals = calculateTotals(lines, adjustments);

  // The company's default decides how a new invoice starts, and it arrives after the first render.
  const [saleTypeChosen, setSaleTypeChosen] = useState<"credit" | "cash" | null>(
    draft ? (draft.cashSale ? "cash" : "credit") : null,
  );
  const saleType = saleTypeChosen ?? (setting?.cashSaleByDefault ? "cash" : "credit");
  // Follows the total until someone types a figure.
  const [typedReceived, setTypedReceived] = useState<string | null>(
    draft?.cashSale?.amountReceived ?? null,
  );
  const received = typedReceived ?? totals.totalWithVat;
  const [method, setMethod] = useState<ReceiptMethod>(draft?.cashSale?.method ?? "Cash");
  const [accountChosen, setAccountChosen] = useState(draft?.cashSale?.bankAccountId ?? "");
  const depositAccount =
    bankAccounts?.find((account) => account.id === accountChosen) ??
    bankAccounts?.find((account) => account.kind === "cash") ??
    bankAccounts?.[0];

  const canSave =
    customer !== null &&
    date !== "" &&
    reference.trim() !== "" &&
    Number(totals.totalWithVat) > 0 &&
    withAccounts.every((line) => line.accountId !== "" && line.description.trim() !== "") &&
    adjustments.every(adjustmentIsComplete) &&
    (saleType === "credit" || (Number(received) > 0 && depositAccount !== undefined)) &&
    !save.isPending;

  function submit() {
    save.mutate(
      {
        id: draft?.id,
        customerId: customer!.id,
        date,
        due: due || undefined,
        paymentTermsDays: terms === "" ? undefined : terms,
        buyerReference: reference.trim(),
        note: note || undefined,
        lines: withAccounts.map((line) => ({
          description: line.description,
          quantity: line.quantity,
          unitPrice: line.grossPrice,
          vatCategory: line.category,
          ratePct: line.rate ?? "0",
          accountId: line.accountId,
          itemCode: line.itemCode,
          exemptionReason: line.exemptionReason,
          billingItemId: line.billingItemId,
          charges: line.charges,
          allowances: line.allowances,
        })),
        adjustments: adjustments.map(({ id: _id, ...adjustment }) => adjustment),
        cashSale:
          saleType === "cash"
            ? { amountReceived: received, method, bankAccountId: depositAccount!.id }
            : undefined,
      },
      {
        onSuccess: (invoice) =>
          void navigate({ to: "/sales/invoices/$id", params: { id: invoice.id } }),
      },
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={draft ? "Edit invoice" : "New invoice"}
        actions={
          <span className="flex gap-2">
            <Button
              colorScheme="default"
              variant="ghost"
              size="sm"
              onClick={() => void navigate(back)}
            >
              Cancel
            </Button>
            <Button
              colorScheme="primary"
              variant="solid"
              size="sm"
              disabled={!canSave}
              onClick={submit}
            >
              {save.isPending ? "Saving" : "Save draft"}
            </Button>
          </span>
        }
      />

      {save.isError ? (
        <p role="alert" className="text-bad-text text-sm">
          {save.error instanceof Error ? save.error.message : "That invoice could not be saved."}
        </p>
      ) : null}

      {customer ? (
        <CreditLimitWarning customer={customer} invoiceTotal={String(totals.totalWithVat)} />
      ) : null}

      <div className="flex flex-col gap-4">
        <SectionCard title="Invoice">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <PartyCombobox
              label="Customer"
              required
              value={customer}
              onChange={(party) => {
                setCustomer(party);
                setTermsOnInvoice(null);
                setTypedDue(null);
                // An invoice cannot be issued without a reference (BR-CO-25), and most have no
                // purchase order to quote. The customer's name is a true answer, in plain sight.
                if (party && reference === "") setReference(party.name);
              }}
              search={(q) => api.parties.search("customer", q)}
            />
            <Select
              label="Sale type"
              options={[
                { value: "credit", label: "Credit" },
                { value: "cash", label: "Cash" },
              ]}
              value={saleType}
              onChange={(details: { value: SelectOption | SelectOption[] | null }) => {
                const option = Array.isArray(details.value) ? details.value[0] : details.value;
                if (option) setSaleTypeChosen(option.value === "cash" ? "cash" : "credit");
              }}
              showClearButton={false}
            />
            <InputText
              label="Customer reference"
              required
              value={reference}
              onChange={(event) => setReference(event.target.value)}
            />
            <InputText
              label="Invoice date"
              type="date"
              required
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />
            <Select
              label="Payment terms"
              options={PAYMENT_TERMS}
              value={terms}
              onChange={(details: { value: SelectOption | SelectOption[] | null }) => {
                const option = Array.isArray(details.value) ? details.value[0] : details.value;
                setTermsOnInvoice(option ? String(option.value) : "");
                setTypedDue(null);
              }}
              showClearButton={false}
            />
            <InputText
              label="Due date"
              type="date"
              value={due}
              onChange={(event) => setTypedDue(event.target.value)}
            />
            <TextArea
              label="Note"
              rows={3}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
        </SectionCard>

        <div className="flex flex-col gap-4">
          <SectionCard title="Lines">
            <LineEditor
              lines={withAccounts}
              onChange={setLines}
              accounts={revenueAccounts}
              items={billingItems}
              showHsCode={setting?.showHsCode ?? false}
              lineDiscounts={
                (setting?.lineDiscounts ?? false) ||
                withAccounts.some((line) => line.allowances || line.charges)
              }
            />
          </SectionCard>
          <SectionCard title="Discounts and charges on the invoice">
            <DocumentAdjustments
              adjustments={adjustments}
              onChange={setAdjustments}
              allowDiscounts={
                (setting?.documentDiscounts ?? false) || adjustments.some((a) => !a.isCharge)
              }
            />
          </SectionCard>
          <div
            className={
              setting?.showVatBreakdown ? "grid gap-4 md:grid-cols-2" : "ml-auto w-full md:w-1/2"
            }
          >
            {setting?.showVatBreakdown ? (
              <SectionCard title={say("VAT breakdown")}>
                <VatBreakdownTable totals={totals} />
              </SectionCard>
            ) : null}
            <div className="flex flex-col gap-4">
              <SectionCard title="Totals">
                <TotalsBlock totals={totals} />
              </SectionCard>
              {saleType === "cash" ? (
                <SectionCard title="Cash receive">
                  <CashReceive
                    total={totals.totalWithVat}
                    received={received}
                    onReceived={setTypedReceived}
                    method={method}
                    onMethod={setMethod}
                    accounts={bankAccounts ?? []}
                    accountId={depositAccount?.id ?? ""}
                    onAccount={setAccountChosen}
                  />
                </SectionCard>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
